import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ExpandMoreOutlined,
  FactCheckOutlined,
  GppGoodOutlined,
  ShieldOutlined,
  TrackChangesOutlined,
  TrendingUpOutlined,
} from '@mui/icons-material';
import { ActionPlanSection } from '../components/safetyReport/ActionPlanSection';
import { SafetyEvidenceCardsSection } from '../components/safetyReport/SafetyEvidenceCard';
import type { SafetyScanBuyerReportPayload, SafetyScanReportV2Payload } from '../services/reportsApi';
import type { SafetyReportOutcomeContext } from './safetyReportOutcomeContext';
import {
  EXTERNAL_DEMO_CORRECTIVE_ACTION_FOLLOW_UP,
  EXTERNAL_DEMO_SUCCESS_METRIC,
  OBSERVED_SIGNALS_EXPLANATION,
  formatExternalFormalClaimStatus,
  sanitizeExternalControlRationale,
  sanitizeExternalLabel,
} from '../domain/externalReview/externalReportLanguage';

function text(value?: string | null): string {
  const normalized = String(value || '').trim();
  return normalized || 'Not available';
}

function formatDateTime(value?: string | null): string {
  const raw = String(value || '').trim();
  if (!raw) return 'Not available';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const ISO_TIMESTAMP_RE = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?/g;

function humanizeEmbeddedTimestamps(value?: string | null): string {
  const raw = String(value || '').trim();
  if (!raw) return 'Not available';
  return raw.replace(ISO_TIMESTAMP_RE, (match) => formatDateTime(match));
}

function formatDecisionWorkflowLabel(report: SafetyScanBuyerReportPayload): string {
  const decision = report.executive_summary?.recommended_decision || {};
  const confidence = report.confidence_posture || {};
  const summary = String(
    decision.workflow_status_summary
      || confidence.workflow_status_summary
      || '',
  ).trim();
  if (summary) return summary;
  const safetyAction = String(decision.safety_action_status || confidence.safety_action_status || '').trim();
  const impactValidation = String(
    decision.impact_validation_status || confidence.impact_validation_status || '',
  ).trim();
  if (safetyAction && impactValidation) {
    if (/needs|pending/i.test(impactValidation)) {
      return `${safetyAction}; impact validation pending`;
    }
    return `${safetyAction}; ${impactValidation}`;
  }
  return safetyAction || impactValidation || 'Ready for EHS review; impact validation pending';
}

function formatImpactConfidenceLabel(value?: string | null): string {
  const raw = text(value);
  if (/not applicable/i.test(raw) || /not yet claimable/i.test(raw)) {
    return 'Not Yet Validated';
  }
  return raw;
}

function displayText(value?: string | null, externalSafe?: boolean, isDemo?: boolean): string {
  const raw = text(value);
  if (!externalSafe || raw === 'Not available') return raw;
  return sanitizeExternalLabel(raw, { isDemo }) || 'Not available';
}

function displayArea(value?: string | null, externalSafe?: boolean, isDemo?: boolean): string {
  const base = customerAreaLabel(value);
  if (!externalSafe || base === 'Not available') return base;
  return sanitizeExternalLabel(base, { isDemo }) || 'Not available';
}

function customerAreaLabel(value?: string | null): string {
  const normalized = String(value || '').trim();
  if (!normalized) return 'Not available';
  const compact = normalized.toLowerCase().replace(/[_\-\s]+/g, '');
  // Intentional alias: demo5tes / Demo test area → Restricted Forklift Zone (basis change 2026-08-05).
  if (compact === 'demo5tes' || compact === 'demotestarea' || compact === 'demo_test_area') {
    return 'Restricted Forklift Zone';
  }
  if (/\bdemo\b/i.test(normalized) || /^demo[a-z0-9_]/i.test(normalized)) {
    return 'Not available';
  }
  if (compact.includes('newfork') && compact.includes('pipel')) {
    return 'Forklift operating area';
  }
  return normalized;
}

function SummaryCard(props: {
  title: string;
  icon: React.ReactNode;
  lines: Array<{ label: string; value: string }>;
  accent?: 'finding' | 'action' | 'claim';
}) {
  const accentBorder = props.accent === 'action'
    ? '#86efac'
    : props.accent === 'claim'
    ? '#fcd34d'
    : '#fca5a5';
  const accentIcon = props.accent === 'action'
    ? '#15803d'
    : props.accent === 'claim'
    ? '#b45309'
    : '#b91c1c';

  return (
    <Card
      sx={{
        borderRadius: 3,
        border: '1px solid #E2E8F0',
        borderTop: `3px solid ${accentBorder}`,
        bgcolor: '#FFFFFF',
        height: '100%',
        boxShadow: 'none',
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <Box sx={{ color: accentIcon, display: 'flex' }}>{props.icon}</Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {props.title}
          </Typography>
        </Stack>
        <Stack spacing={1}>
          {props.lines.map((line) => (
            <Box key={line.label}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {line.label}
              </Typography>
              <Typography variant="body2">{line.value}</Typography>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

const accordionSx = {
  border: '1px solid #E2E8F0',
  borderRadius: '12px !important',
  mb: 1.5,
  '&:before': { display: 'none' },
  boxShadow: 'none',
} as const;

export function BuyerReportSection(props: {
  data?: SafetyScanBuyerReportPayload | null;
  outcomeContext?: SafetyReportOutcomeContext | null;
  externalSafe?: boolean;
  buyerFacing?: boolean;
  safetyScanReportV2?: SafetyScanReportV2Payload | null;
}) {
  const report = props.data;
  const outcomeContext = props.outcomeContext || null;
  const externalSafe = Boolean(props.externalSafe);
  const buyerFacing = Boolean(props.buyerFacing);
  const isDemoSource = Boolean(outcomeContext?.isDemoOrLoopedSource);
  if (!report?.executive_summary) return null;

  const executive = report.executive_summary;
  const finding = executive.safety_finding || {};
  const decision = executive.recommended_decision || {};
  const boundary = executive.claim_boundary || {};
  const confidence = report.confidence_posture || {};
  const impact = report.impact_measurement_interpretation || {};
  const followUp = report.follow_up_scan_plan || {};
  const claimItems = report.claim_boundary_summary?.items || [];
  const controls = report.corrective_action_controls?.recommended_controls || [];

  const followUpSteps = [
    `Use the same monitored area: ${followUp.same_zones ? 'Yes' : 'Review zone coverage'}`,
    `Use comparable baseline and follow-up windows: ${text(followUp.recommended_window_display) || humanizeEmbeddedTimestamps(followUp.recommended_window)}`,
    externalSafe && isDemoSource
      ? EXTERNAL_DEMO_CORRECTIVE_ACTION_FOLLOW_UP
      : `Confirm corrective action remains in place: ${text(followUp.corrective_action_verified)}`,
    externalSafe && isDemoSource
      ? 'Production measurement requires live site data and matched follow-up windows.'
      : 'Compare normalized exposure rates.',
    externalSafe && isDemoSource
      ? EXTERNAL_DEMO_SUCCESS_METRIC
      : `Success metric: ${text(followUp.success_metric)}`,
    ...(externalSafe && !isDemoSource
      ? ['Require sufficient monitored observation time before stronger claims.']
      : []),
  ].map((step) => (externalSafe ? displayText(step, true, isDemoSource) : step));

  const evidenceCards = props.safetyScanReportV2?.evidence_cards;
  const actionPlan = props.safetyScanReportV2?.action_plan;

  const findingNarrative = displayText(
    finding.why_it_matters || finding.risk_detected,
    externalSafe,
    isDemoSource,
  );
  const peakConcentration = externalSafe
    ? displayText(String(finding.event_signals ?? 'Not recorded'), externalSafe, isDemoSource)
    : String(finding.event_signals ?? 'Not recorded');

  if (buyerFacing) {
    return (
      <Box sx={{ mb: 3 }} data-testid="buyer-report-compact-face">
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
            Recurring pattern
          </Typography>
          <Typography variant="body2" color="text.secondary">
            What this scan measured, where concentration appeared, and what deserves field review.
          </Typography>
        </Stack>

        <Card
          sx={{
            mb: 2,
            borderRadius: 3,
            border: '1px solid #E2E8F0',
            borderTop: '3px solid #fca5a5',
            bgcolor: '#FFFFFF',
            boxShadow: 'none',
          }}
        >
          <CardContent>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 500, color: '#0f172a' }}>
              {findingNarrative}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700, letterSpacing: '0.04em' }}>
                  Where
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 600 }}>
                  {displayArea(finding.primary_hotspot, externalSafe, isDemoSource)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700, letterSpacing: '0.04em' }}>
                  When
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 600 }}>
                  {displayText(finding.peak_period, externalSafe, isDemoSource)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700, letterSpacing: '0.04em' }}>
                  Peak concentration
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.35, fontWeight: 600 }}>
                  {peakConcentration}
                </Typography>
              </Box>
            </Box>
            {text(boundary.summary) !== 'Not available' ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {displayText(boundary.summary, externalSafe, isDemoSource)}
              </Typography>
            ) : null}
          </CardContent>
        </Card>

        {props.safetyScanReportV2 ? (
          <SafetyEvidenceCardsSection
            cards={evidenceCards}
            buyerFacing
            title="Evidence record"
            subtitle="Representative snapshots from this scan. Full evidence remains in the signed package."
          />
        ) : null}

        <Card sx={{ mb: 2, borderRadius: 3, border: '1px solid #E2E8F0', borderTop: '3px solid #86efac', boxShadow: 'none' }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
              <TrackChangesOutlined sx={{ color: '#15803d', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                What to do next
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
              {displayText(decision.decision, externalSafe, isDemoSource)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {displayText(decision.immediate_action, externalSafe, isDemoSource)}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.1 }}>
              {followUpSteps.map((step) => (
                <Box component="li" key={step} sx={{ mb: 0.6 }}>
                  <Typography variant="body2">{step}</Typography>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        {props.safetyScanReportV2 ? (
          <ActionPlanSection
            actionPlan={actionPlan}
            title="Control decision"
            subtitle="Selected controls and implementation status from the signed scan."
          />
        ) : null}

        <Accordion defaultExpanded={false} disableGutters elevation={0} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Site risk-assessment boundary
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              <Typography variant="body2">
                {displayText((boundary.supported_claims || []).join('; ') || 'None listed', externalSafe, isDemoSource)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {displayText((boundary.not_supported_claims || []).join('; ') || 'None listed', externalSafe, isDemoSource)}
              </Typography>
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded={false} disableGutters elevation={0} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TrendingUpOutlined sx={{ color: '#64748b', fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {isDemoSource ? 'What this demo demonstrates' : 'What the measurement means'}
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {isDemoSource ? (
                <>
                  <Typography variant="body2">
                    Workflow impact: Demonstrated across detection, review, corrective-action logging, Signed Safety Scan delivery, and export packaging.
                  </Typography>
                  <Typography variant="body2">
                    Exposure reduction: Not evaluated on looped demo footage.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Production measurement: Supported on live site data using matched follow-up windows and trusted monitored observation time.
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2">
                    Baseline count: {impact.baseline_count ?? 'Not recorded'} · Follow-up count:{' '}
                    {impact.followup_count ?? 'Not recorded'}
                  </Typography>
                  <Typography variant="body2">{text(impact.directional_change_summary)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {text(impact.claim_withheld_reason)}
                  </Typography>
                </>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {controls.length > 0 ? (
          <Accordion defaultExpanded={false} disableGutters elevation={0} sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FactCheckOutlined sx={{ color: '#64748b', fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Hierarchy of controls
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {text(report.corrective_action_controls?.administrative_note)}
              </Typography>
              <Stack spacing={2}>
                {controls.map((control, index) => (
                  <Box key={`${control.level}-${control.label}`}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {`${index + 1}. ${text(control.label)}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {externalSafe
                        ? sanitizeExternalControlRationale(control.level, text(control.rationale), {
                            isDemo: isDemoSource,
                          })
                        : text(control.rationale)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ) : null}

        {claimItems.length > 0 ? (
          <Accordion defaultExpanded={false} disableGutters elevation={0} sx={accordionSx}>
            <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Claim boundaries detail
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Divider sx={{ mb: 1.5 }} />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Claim</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Note</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {claimItems.map((item) => (
                    <TableRow key={item.claim}>
                      <TableCell>{displayText(item.claim, externalSafe, isDemoSource)}</TableCell>
                      <TableCell>
                        {externalSafe
                          ? formatExternalFormalClaimStatus(item.status, { isDemo: isDemoSource })
                          : text(item.status)}
                      </TableCell>
                      <TableCell>{displayText(item.note, externalSafe, isDemoSource)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        ) : null}
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Stack spacing={1} sx={{ mb: 2 }}>
        <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
          {externalSafe ? 'Recommended safety decision and claim boundaries' : 'Safety finding and recommended safety decision'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The three conclusions operations and EHS should take from this Signed Safety Scan.
        </Typography>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
          mb: 2,
        }}
      >
        <SummaryCard
          title={externalSafe ? 'Workplace condition observed' : 'Safety finding'}
          icon={<ShieldOutlined />}
          accent="finding"
          lines={[
            {
              label: externalSafe ? 'Workplace condition observed' : 'Risk detected',
              value: displayText(finding.risk_detected, externalSafe, isDemoSource),
            },
            {
              label: 'Measured signals',
              value: externalSafe
                ? `${displayText(String(finding.event_signals ?? 'Not recorded'), externalSafe, isDemoSource)}. ${OBSERVED_SIGNALS_EXPLANATION}`
                : `${String(finding.event_signals ?? 'Not recorded')}. Multiple signals may belong to the same real-world exposure event.`,
            },
            { label: 'Primary hotspot', value: displayArea(finding.primary_hotspot, externalSafe, isDemoSource) },
            { label: 'Peak period', value: displayText(finding.peak_period, externalSafe, isDemoSource) },
            { label: 'Why this matters', value: displayText(finding.why_it_matters, externalSafe, isDemoSource) },
          ]}
        />
        <SummaryCard
          title="Recommended safety decision"
          icon={<TrackChangesOutlined />}
          accent="action"
          lines={[
            { label: 'Decision', value: displayText(decision.decision, externalSafe, isDemoSource) },
            { label: 'Immediate action', value: displayText(decision.immediate_action, externalSafe, isDemoSource) },
            ...(externalSafe
              ? []
              : [{ label: 'Decision workflow', value: text(decision.decision_readiness) }]),
          ]}
        />
        <SummaryCard
          title={externalSafe ? 'Claim boundaries' : 'Claim status'}
          icon={<GppGoodOutlined />}
          accent="claim"
          lines={[
            { label: 'Finding status', value: displayText(boundary.summary, externalSafe, isDemoSource) },
            {
              label: 'What we can claim',
              value: displayText((boundary.supported_claims || []).join('; ') || 'None listed', externalSafe, isDemoSource),
            },
            {
              label: externalSafe ? 'What this report cannot claim' : 'What we cannot claim yet',
              value: displayText((boundary.not_supported_claims || []).join('; ') || 'None listed', externalSafe, isDemoSource),
            },
          ]}
        />
      </Box>

      {externalSafe ? null : isDemoSource ? (
        <Card sx={{ mb: 2, borderRadius: 3, border: '1px solid #BFDBFE', bgcolor: '#F8FAFC', boxShadow: 'none' }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Demo source context
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }} data-testid="buyer-demo-source-paragraph">
              {outcomeContext?.buyerDemoParagraph}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={`Finding: ${text(confidence.finding_confidence)}`} size="small" variant="outlined" />
              <Chip label="Workflow impact: Demonstrated" size="small" variant="outlined" />
              <Chip label="Exposure reduction: Not evaluated on looped demo footage" size="small" variant="outlined" />
              <Chip label="Corrective-action effectiveness: Not claimed from demo source" size="small" variant="outlined" />
              <Chip label="Production measurement: Supported on live site data" size="small" variant="outlined" />
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ mb: 2, borderRadius: 3, border: '1px solid #E2E8F0', boxShadow: 'none' }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Confidence posture
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              <Chip label={`Finding: ${text(confidence.finding_confidence)}`} size="small" variant="outlined" />
              <Chip label={`Impact: ${formatImpactConfidenceLabel(confidence.impact_confidence)}`} size="small" variant="outlined" />
              <Chip label={`Improvement: ${text(confidence.improvement_claim_status)}`} size="small" variant="outlined" />
              <Chip label={`Decision workflow: ${formatDecisionWorkflowLabel(report)}`} size="small" variant="outlined" />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Finding confidence is {text(confidence.finding_confidence).toLowerCase()}. Impact confidence is {formatImpactConfidenceLabel(confidence.impact_confidence).toLowerCase()}.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              Do not claim accident prevention, corrective-action effectiveness, or financial ROI from this report alone.
            </Typography>
          </CardContent>
        </Card>
      )}

      <Card sx={{ mb: 2, borderRadius: 3, border: '1px solid #E2E8F0', boxShadow: 'none' }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.25 }}>
            Follow-up scan plan
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.1 }}>
            {followUpSteps.map((step) => (
              <Box component="li" key={step} sx={{ mb: 0.6 }}>
                <Typography variant="body2">{step}</Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      <Accordion defaultExpanded={false} disableGutters elevation={0} sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <TrendingUpOutlined sx={{ color: '#64748b', fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {isDemoSource ? 'What this demo demonstrates' : 'What the measurement means'}
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1}>
            {isDemoSource ? (
              <>
                <Typography variant="body2">
                  Workflow impact: Demonstrated across detection, review, corrective-action logging, Signed Safety Scan delivery, and export packaging.
                </Typography>
                <Typography variant="body2">
                  Exposure reduction: Not evaluated on looped demo footage.
                </Typography>
                <Typography variant="body2">
                  Corrective-action effectiveness: Not claimed from demo source.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Production measurement: Supported on live site data using matched follow-up windows and trusted monitored observation time.
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body2">
                  Baseline count: {impact.baseline_count ?? 'Not recorded'} · Follow-up count:{' '}
                  {impact.followup_count ?? 'Not recorded'}
                </Typography>
                <Typography variant="body2">{text(impact.directional_change_summary)}</Typography>
                <Typography variant="body2">
                  {externalSafe ? 'Claim boundaries' : 'Claim status'}:{' '}
                  {externalSafe
                    ? formatExternalFormalClaimStatus(impact.formal_claim_status, { isDemo: isDemoSource })
                    : text(impact.formal_claim_status)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {text(impact.claim_withheld_reason)}
                </Typography>
              </>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion defaultExpanded={false} disableGutters elevation={0} sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <FactCheckOutlined sx={{ color: '#64748b', fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Hierarchy of controls
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {text(report.corrective_action_controls?.administrative_note)}
          </Typography>
          <Stack spacing={2}>
            {controls.map((control, index) => (
              <Box key={`${control.level}-${control.label}`}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {`${index + 1}. ${text(control.label)}`}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {externalSafe
                    ? sanitizeExternalControlRationale(control.level, text(control.rationale), {
                        isDemo: isDemoSource,
                      })
                    : text(control.rationale)}
                </Typography>
                {(control.examples || []).length > 0 ? (
                  <Typography variant="body2">{(control.examples || []).join(' · ')}</Typography>
                ) : null}
              </Box>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {claimItems.length > 0 ? (
        <Accordion defaultExpanded={false} disableGutters elevation={0} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {externalSafe ? 'Claim boundaries' : 'What this report can and cannot claim'}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Divider sx={{ mb: 1.5 }} />
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Claim</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {claimItems.map((item) => (
                  <TableRow key={item.claim}>
                    <TableCell>{displayText(item.claim, externalSafe, isDemoSource)}</TableCell>
                    <TableCell>
                      {externalSafe
                        ? formatExternalFormalClaimStatus(item.status, { isDemo: isDemoSource })
                        : text(item.status)}
                    </TableCell>
                    <TableCell>{displayText(item.note, externalSafe, isDemoSource)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      ) : null}

      {props.safetyScanReportV2 ? (
        <>
          <SafetyEvidenceCardsSection cards={props.safetyScanReportV2.evidence_cards} />
          <ActionPlanSection actionPlan={props.safetyScanReportV2.action_plan} />
        </>
      ) : null}
    </Box>
  );
}
